/**
 * The data API client. Every screen in this application reads through here.
 *
 * There is no model call on this path and that is the design, not an omission: ADR 0004. A list
 * of appointments is a SQL query — about 20 ms and zero tokens. Asking a language model to
 * fetch and read it back costs seconds and thousands of tokens to recite a number it was handed.
 * The agent is for language. Lists are for SQL.
 */
import { headersFor, type Identity } from './identity'
import type { AppointmentList, ServiceList, ToolError, Whoami } from './types'

const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function get<T>(path: string, identity: Identity, signal?: AbortSignal): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, { headers: headersFor(identity), signal })
  } catch {
    // A network failure is not a domain error, and saying "not permitted" for one would send
    // somebody looking at permissions for an hour.
    throw new ApiError('UNREACHABLE', 'the service could not be reached', 0)
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ToolError | null
    throw new ApiError(
      body?.code ?? 'HTTP_ERROR',
      body?.message ?? `the service answered ${response.status}`,
      response.status,
    )
  }
  return (await response.json()) as T
}

async function post<T>(path: string, identity: Identity, body: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { ...headersFor(identity), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new ApiError('UNREACHABLE', 'the service could not be reached', 0)
  }
  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as ToolError | null
    throw new ApiError(
      failure?.code ?? 'HTTP_ERROR',
      failure?.message ?? `the service answered ${response.status}`,
      response.status,
    )
  }
  return (await response.json()) as T
}

export const api = {
  me: (identity: Identity, signal?: AbortSignal) => get<Whoami>('/api/me', identity, signal),
  /**
   * One turn of conversation with the same assistant the telephone reaches.
   *
   * No session identifier is sent. The server derives it from the signed-in subject, because a
   * browser that could choose its own would be choosing whose conversation to join.
   */
  chat: (identity: Identity, text: string) =>
    post<{ ok?: boolean; reply?: string; message?: string }>('/api/chat', identity, { text }),
  services: (identity: Identity, signal?: AbortSignal) =>
    get<ServiceList>('/api/services', identity, signal),
  appointments: (identity: Identity, signal?: AbortSignal) =>
    get<AppointmentList>('/api/appointments', identity, signal),
}
